import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { Message } from 'ai'

export interface CopilotConversation {
  id: string
  title: string
  scope?: string
  interfaceId?: number | null
  interfaceName?: string | null
  userId: number | null
  createdAt: number
  updatedAt: number
  messages: Message[]
}

export type CopilotUserId = number | null

interface CopilotConversationDB extends DBSchema {
  conversations: {
    key: string
    value: CopilotConversation
    indexes: { 'by-updatedAt': number }
  }
}

const DB_NAME = 'cisgenx.copilot'
const DB_VERSION = 1
const STORE = 'conversations'
const INDEX = 'by-updatedAt'
const CHANNEL_NAME = 'cisgenx.copilot.conversations'

let dbPromise: Promise<IDBPDatabase<CopilotConversationDB>> | null = null
let broadcastChannel: BroadcastChannel | null = null

const getChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') return null
  if (!broadcastChannel) {
    try {
      broadcastChannel = new BroadcastChannel(CHANNEL_NAME)
    } catch (error) {
      console.error('Failed to open copilot BroadcastChannel:', error)
      return null
    }
  }
  return broadcastChannel
}

const emitChange = (): void => {
  try {
    getChannel()?.postMessage({ type: 'changed' })
  } catch (error) {
    console.error('Failed to broadcast copilot conversation change:', error)
  }
}

export const subscribeToConversationChanges = (
  handler: () => void,
): (() => void) => {
  const ch = getChannel()
  if (!ch) return () => undefined
  const listener = (event: MessageEvent) => {
    if (
      event.data &&
      typeof event.data === 'object' &&
      (event.data as { type?: unknown }).type === 'changed'
    ) {
      handler()
    }
  }
  ch.addEventListener('message', listener)
  return () => {
    try {
      ch.removeEventListener('message', listener)
    } catch (error) {
      console.error('Failed to unsubscribe copilot conversation listener:', error)
    }
  }
}

const getDB = () => {
  if (!dbPromise) {
    dbPromise = openDB<CopilotConversationDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id' })
          store.createIndex(INDEX, 'updatedAt')
        }
      },
    })
  }
  return dbPromise
}

const reviveMessages = (messages: Message[]): Message[] =>
  messages.map((m) => ({
    ...m,
    createdAt: m.createdAt ? new Date(m.createdAt) : undefined,
  }))

const reviveConversation = (
  conv: CopilotConversation,
): CopilotConversation => ({
  ...conv,
  messages: reviveMessages(conv.messages ?? []),
})

export const listConversations = async (
  userId: CopilotUserId,
): Promise<CopilotConversation[]> => {
  try {
    const db = await getDB()
    const all = await db.getAllFromIndex(STORE, INDEX)
    return all
      .filter((conv) => (conv.userId ?? null) === userId)
      .reverse()
      .map(reviveConversation)
  } catch (error) {
    console.error('Failed to list copilot conversations:', error)
    return []
  }
}

export const getConversation = async (
  id: string,
  userId: CopilotUserId,
): Promise<CopilotConversation | null> => {
  try {
    const db = await getDB()
    const conv = await db.get(STORE, id)
    if (!conv) return null
    if ((conv.userId ?? null) !== userId) return null
    return reviveConversation(conv)
  } catch (error) {
    console.error(`Failed to get copilot conversation ${id}:`, error)
    return null
  }
}

export const upsertConversation = async (
  conv: CopilotConversation,
): Promise<void> => {
  try {
    const db = await getDB()
    await db.put(STORE, { ...conv, updatedAt: Date.now() })
    emitChange()
  } catch (error) {
    console.error('Failed to upsert copilot conversation:', error)
  }
}

export const deleteConversation = async (
  id: string,
  userId: CopilotUserId,
): Promise<void> => {
  try {
    const db = await getDB()
    const conv = await db.get(STORE, id)
    if (!conv) return
    if ((conv.userId ?? null) !== userId) return
    await db.delete(STORE, id)
    emitChange()
  } catch (error) {
    console.error(`Failed to delete copilot conversation ${id}:`, error)
  }
}

export const renameConversation = async (
  id: string,
  title: string,
  userId: CopilotUserId,
): Promise<void> => {
  try {
    const db = await getDB()
    const conv = await db.get(STORE, id)
    if (!conv) return
    if ((conv.userId ?? null) !== userId) return
    await db.put(STORE, { ...conv, title, updatedAt: Date.now() })
    emitChange()
  } catch (error) {
    console.error(`Failed to rename copilot conversation ${id}:`, error)
  }
}

export const buildConversationTitle = (messages: Message[]): string => {
  const firstUser = messages.find((m) => m.role === 'user')
  const text =
    typeof firstUser?.content === 'string' ? firstUser.content.trim() : ''
  if (!text) return 'New conversation'
  if (text.startsWith('CONFIRM_TOOL')) return 'New conversation'
  return text.length > 60 ? `${text.slice(0, 60).trimEnd()}…` : text
}

export const generateConversationId = (): string => {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }
  return `conv_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
}
